import { onUnmounted, type Ref, ref, watch } from 'vue';

export type SelectDismissOptions = {
  /** The whole field, so clicks on its padding and icons count as "inside". */
  fieldElement: () => Element | null | undefined;
  /** The dropdown body, so clicks on an option count as "inside". */
  listElement: () => Element | null | undefined;
};

/**
 * How a select-shaped field gets dismissed: Escape, a pointer outside, and the
 * listener bookkeeping around both.
 *
 * Extracted because `NxSelectInput` and `NxFilterSelectInput` carried it twice,
 * near-verbatim — which is how the two drifted apart on Escape, where only one of
 * them lived up to the promise that it cancels. The dismiss policy is now decided
 * in one place; each component keeps only what its own trigger needs (a typeahead
 * buffer, a query) in its own `isOpen` watcher.
 *
 * `NxDropdown` deliberately implements no click-outside of its own: a generic
 * document-level closer would fire before a consumer's selection handling and
 * swallow the pick (see its docblock). This is that policy, written once for the
 * two consumers that need the same one.
 */
export function useSelectDismiss(isOpen: Ref<boolean>, options: SelectDismissOptions) {
  /**
   * Whether the session now closing was abandoned with Escape. Owned here because
   * this is where Escape is handled; both selects used to declare, reset and read
   * their own copy of it, which is the duplication this composable exists to end.
   */
  const cancelled = ref(false);

  function onKeydown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return;
    e.stopImmediatePropagation();
    cancelled.value = true;
    isOpen.value = false;
  }

  function onPointerDown(e: PointerEvent) {
    const target = e.target as Node;
    const field = options.fieldElement();
    const list = options.listElement();
    if (!field?.contains(target) && !list?.contains(target)) {
      isOpen.value = false;
    }
  }

  function stopListening() {
    document.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('pointerdown', onPointerDown, true);
  }

  watch(isOpen, (open) => {
    if (open) {
      // Reset on open, never on close: the consumer's own close handler runs
      // after this one and has to be able to read how the field was dismissed.
      cancelled.value = false;
      document.addEventListener('keydown', onKeydown, true);
      document.addEventListener('pointerdown', onPointerDown, true);
      // Scroll the field into view so the dropdown is guaranteed visible.
      options.fieldElement()?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    } else {
      stopListening();
    }
  });

  onUnmounted(stopListening);

  return { cancelled };
}
